/**
 * The coffee card on the dashboard — this module's card type, behind
 * `coffee.use`. Its one setting, `machine`, is a machine's id or `all`.
 *
 * - **`all`: one machine at a time**, with arrows to the others when there
 *   are several. The one last looked at is remembered in this browser, so
 *   each person lands on their own floor's machine.
 * - **A machine's id: that machine**, with no arrows. A machine that is gone
 *   renders nothing.
 * - **Nothing at all while loading, when there is no machine, or when the list
 *   fails.** The dashboard is not the place to explain a coffee outage, and a
 *   person with no machine sees the dashboard exactly as before.
 * - It renders inside an `ErrorBoundary` (see `module.tsx`), so a crash here
 *   costs this card and nothing else.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAsync } from '../../hooks/useAsync';
import type { CoffeeMachineDto } from '@forsys/shared';
import type { DashboardOption } from '../types';
import { listMachines } from './api';
import { MachineCard } from './MachineCard';

const STORAGE_KEY = 'forsys.coffee.dashboardMachine';

/** Browser storage can be missing or refuse; the card works without it. */
function remembered(): number | null {
  try {
    const value = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function remember(id: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    // Not remembered; the next visit starts at the first machine.
  }
}

export type CoffeeCardConfig = { readonly machine: string };

/** The card's choices: every machine one at a time, or one of them. */
export async function machineOptions(): Promise<DashboardOption[]> {
  const { machines } = await listMachines();
  return [
    { value: 'all', labelKey: 'coffee:dashboard.allMachines' },
    ...machines.map((machine) => ({ value: String(machine.id), label: machine.name })),
  ];
}

export function DashboardCoffee({ config }: { readonly config: CoffeeCardConfig }) {
  const machines = useAsync(listMachines, []);

  const list = machines.data?.machines ?? [];
  if (machines.error || list.length === 0) return null;
  if (config.machine === 'all') return <Carousel list={list} />;

  const machine = list.find((candidate) => String(candidate.id) === config.machine);
  return machine ? <MachineCard key={machine.id} machine={machine} /> : null;
}

function Carousel({ list }: { readonly list: readonly CoffeeMachineDto[] }) {
  const { t } = useTranslation('coffee');
  const [chosen, setChosen] = useState<number | null>(remembered);

  const found = list.findIndex((machine) => machine.id === chosen);
  const index = found === -1 ? 0 : found;
  const machine = list[index];
  if (!machine) return null;

  const go = (step: number) => {
    const next = list[(index + step + list.length) % list.length];
    if (!next) return;
    setChosen(next.id);
    remember(next.id);
  };

  const controls =
    list.length > 1 ? (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <IconButton size="small" aria-label={t('machine.previous')} onClick={() => go(-1)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {t('machine.position', { current: index + 1, total: list.length })}
        </Typography>
        <IconButton size="small" aria-label={t('machine.next')} onClick={() => go(1)}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
    ) : undefined;

  return <MachineCard key={machine.id} machine={machine} controls={controls} />;
}
